import type React from "react";
import {
  Box,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { Add } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { IService } from "../../../../types/package";
import { useThemeColors } from "../hooks/UseThemeColors";
import ServiceTableRow from "./ServiceTablesRow";
import PaginationControls from "./PaginationControl";

interface ServicesSectionProps {
  activeTab: number;
  onTabChange: (index: number) => void;
  services: IService[];
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  onAddService?: () => void;
  onEditService: (service: IService) => void;
  currentPage: number;
  totalCount: number;
  onNextPage: () => void;
  onPreviousPage: () => void;
  categoryFilter?: string;
  onCategoryFilterChange?: (value: string) => void;
}

const serviceTabs = ["Screens", "Video & Audio", "AV Equipment", "Design", "Other", "Old Mutual"];

const ServicesSection: React.FC<ServicesSectionProps> = ({
  activeTab,
  onTabChange,
  services,
  isLoading,
  error,
  onRetry,
  onAddService,
  onEditService,
  currentPage,
  totalCount,
  onNextPage,
  onPreviousPage,
  categoryFilter = "",
  onCategoryFilterChange,
}) => {
  const {
    primaryColor,
    primaryHover,
    primaryTextColor,
    secondaryHighlight,
    whiteColor,
    neutralBackground,
  } = useThemeColors();

  const totalPages = Math.ceil(totalCount / 10);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => onTabChange(newValue)}
          sx={{
            "& .MuiTabs-indicator": { backgroundColor: primaryColor },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: "medium",
              fontSize: "1rem",
              color: primaryTextColor,
              "&.Mui-selected": { color: primaryColor },
            },
          }}
        >
          {serviceTabs.map((tab, index) => (
            <Tab key={index} label={tab} />
          ))}
        </Tabs>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {onCategoryFilterChange && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="service-category-filter-label">
                Package Category
              </InputLabel>
              <Select
                labelId="service-category-filter-label"
                label="Package Category"
                value={categoryFilter}
                onChange={(e: SelectChangeEvent) =>
                  onCategoryFilterChange(e.target.value)
                }
              >
                <MenuItem value="">All Categories</MenuItem>
                <MenuItem value="all">All (universal)</MenuItem>
                <MenuItem value="funeral">Funeral</MenuItem>
                <MenuItem value="funeral_home">Funeral Home</MenuItem>
                <MenuItem value="conference">Conference</MenuItem>
              </Select>
            </FormControl>
          )}
          {onAddService && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={onAddService}
              sx={{
                backgroundColor: primaryColor,
                color: whiteColor,
                textTransform: "none",
                "&:hover": { backgroundColor: primaryHover },
              }}
            >
              ADD SERVICE
            </Button>
          )}
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress sx={{ color: primaryColor }} />
        </Box>
      ) : error ? (
        <Alert
          severity="error"
          sx={{ mb: 2, color: primaryTextColor, bgcolor: neutralBackground }}
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      ) : (
        <>
          <TableContainer
            sx={{
              boxShadow: `0 2px 8px ${alpha(neutralBackground, 0.08)}`,
              backgroundColor: whiteColor,
            }}
          >
            <Table>
              <TableHead sx={{ backgroundColor: neutralBackground }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Service Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Variation</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {services.length > 0 ? (
                  services.map((service) => (
                    <ServiceTableRow
                      key={service.id}
                      service={service}
                      onEdit={onEditService}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      sx={{
                        textAlign: "center",
                        py: 4,
                        color: secondaryHighlight,
                      }}
                    >
                      No services available for this category.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {totalCount > 10 && (
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              onPrevious={onPreviousPage}
              onNext={onNextPage}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default ServicesSection;
